import { useEffect, useRef, useState, type FormEvent, type KeyboardEvent } from "react";
import type { MessageKey } from "@wato/i18n";
import { useI18n } from "@wato/i18n/react";
import { Button, Icon, cx } from "@wato/ui";
import { useWorkspace } from "../workspace/WorkspaceContext";
import { AssistantActionCard } from "./AssistantActionCard";
import { AssistantMessageText } from "./AssistantMessageText";
import { AssistantTodaySuggestions } from "./AssistantTodaySuggestions";
import { useOwnerAssistant } from "./OwnerAssistantProvider";
import { SUGGESTED_QUESTION_KEYS } from "./suggested-questions";

const key = (suffix: string) => `dashboard.ownerAssistant.${suffix}` as MessageKey;

/** OWN-163 chat body (bubbles, loading, error, input). State lives in the provider. */
export function OwnerAssistantChat() {
  const { t } = useI18n();
  const { workspace } = useWorkspace();
  const { turns, busy, error, ask } = useOwnerAssistant();
  const [text, setText] = useState("");
  const listRef = useRef<HTMLOListElement>(null);

  useEffect(() => {
    const el = listRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [turns.length, busy]);

  const submit = (event?: FormEvent) => {
    event?.preventDefault();
    const message = text.trim();
    if (!message || busy) return;
    setText("");
    ask(message);
  };
  const onKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === "Enter" && !event.shiftKey && !event.nativeEvent.isComposing) {
      event.preventDefault();
      submit();
    }
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3" data-testid="owner-assistant-chat">
      <ol
        ref={listRef}
        className="m-0 flex min-h-0 flex-1 list-none flex-col gap-3 overflow-y-auto p-0"
        aria-live="polite"
      >
        {turns.length === 0 ? (
          <li className="flex flex-col gap-3" data-testid="assistant-empty">
            <span
              aria-hidden="true"
              className="flex size-12 items-center justify-center rounded-xl bg-surface-sunken text-fg"
            >
              <Icon name="sparkles" />
            </span>
            <h3 className="m-0 text-lg font-semibold text-fg">{t(key("emptyTitle"))}</h3>
            <p className="m-0 text-sm text-fg-muted">{t(key("lead"))}</p>
            <div className="flex flex-col gap-2">
              <span className="text-xs font-medium text-fg-muted">{t(key("chipsLabel"))}</span>
              <ul className="m-0 flex list-none flex-wrap gap-2 p-0" data-testid="assistant-chips">
                {SUGGESTED_QUESTION_KEYS.map((k) => (
                  <li key={k}>
                    <Button
                      variant="secondary"
                      size="sm"
                      className="h-auto! whitespace-normal! py-1.5 text-start"
                      onClick={() => ask(t(k))}
                    >
                      {t(k)}
                    </Button>
                  </li>
                ))}
              </ul>
            </div>
            <AssistantTodaySuggestions workspaceId={workspace.id} />
            <p className="m-0 text-xs text-fg-muted">{t(key("shortcutHint"))}</p>
          </li>
        ) : null}
        {turns.map((turn) => (
          <li key={turn.id} className="flex flex-col gap-2">
            <span className="text-xs font-medium text-fg-muted">
              {t(key(turn.role === "user" ? "you" : "assistant"))}
            </span>
            <div
              className={cx(
                "rounded-2xl px-4 py-3",
                turn.role === "user" ? "bg-surface-sunken" : "border border-line bg-surface",
              )}
            >
              {turn.role === "user" ? (
                <p dir="auto" className="m-0 whitespace-pre-wrap text-sm">
                  {turn.text}
                </p>
              ) : (
                <AssistantMessageText text={turn.text} workspaceId={workspace.id} />
              )}
            </div>
            {turn.role === "assistant"
              ? turn.actions.map((action) => (
                  <AssistantActionCard key={action.id} action={action} />
                ))
              : null}
          </li>
        ))}
        {busy ? <li className="text-sm text-fg-muted">{t(key("thinking"))}</li> : null}
      </ol>
      {error ? (
        <p role="alert" className="m-0 text-sm text-danger-fg">
          {error}
        </p>
      ) : null}
      <form className="flex items-end gap-2" onSubmit={submit}>
        <textarea
          dir="auto"
          className="min-h-16 flex-1 rounded-xl border border-transparent bg-surface-sunken p-3 text-sm"
          value={text}
          placeholder={t(key("placeholder"))}
          aria-label={t(key("title"))}
          maxLength={2000}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={onKeyDown}
        />
        <Button type="submit" disabled={busy || text.trim() === ""}>
          {t(key("send"))}
        </Button>
      </form>
    </div>
  );
}
